import { useState, useEffect } from "react";

export default function TenantSetup({ supabase }) {
  const [session,  setSession]  = useState(null);
  const [authDone, setAuthDone] = useState(false); // true once getSession resolves

  // Listen for Supabase to process the invite token from the URL hash.
  // onAuthStateChange fires with SIGNED_IN once the token is exchanged.
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, sess) => {
      setSession(sess);
      setAuthDone(true);
    });
    supabase.auth.getSession().then(({ data: { session: sess } }) => {
      setSession(sess);
      setAuthDone(true);
    });
    return () => subscription.unsubscribe();
  }, [supabase]);

  // ── States ────────────────────────────────────────────────────────────────────
  if (!authDone) return <Splash />;
  if (!session)  return <ExpiredLink />;
  // Tenant portal registration is not yet open to new tenants.
  // Existing tenants log in via the sign-in modal (Tenant Portal tab).
  // Remove this line and restore <SetupForm> when ready to open registration.
  return <ComingSoon />;
  // return <SetupForm supabase={supabase} session={session} />;
}

// ── Setup form ────────────────────────────────────────────────────────────────
function SetupForm({ supabase, session }) {
  const user  = session.user;
  const email = user?.email ?? "";

  const [fullName, setFullName] = useState(user?.user_metadata?.full_name ?? "");
  const [password, setPassword] = useState("");
  const [confirm,  setConfirm]  = useState("");
  const [saving,   setSaving]   = useState(false);
  const [done,     setDone]     = useState(false);
  const [error,    setError]    = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!fullName.trim())     { setError("Please enter your full name."); return; }
    if (password.length < 8)  { setError("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setError("Passwords do not match."); return; }

    setSaving(true);
    try {
      const { error: updateErr } = await supabase.auth.updateUser({
        password,
        data: { full_name: fullName.trim() },
      });
      if (updateErr) throw new Error(updateErr.message);
      setDone(true);
      setTimeout(() => { window.location.href = "/tenant"; }, 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const field = {
    background: "#FFFFFF", border: "1px solid #D4CFC7", borderRadius: 8,
    color: "#2B2B2B", padding: "11px 14px", fontSize: 15,
    fontFamily: "'Manrope', sans-serif", width: "100%",
    outline: "none", boxSizing: "border-box",
  };
  const lbl = {
    fontSize: 12, color: "#2B2B2B", fontFamily: "'Manrope', sans-serif",
    fontWeight: 600, marginBottom: 5, display: "block",
  };

  return (
    <Shell>
      {done ? (
        <div style={{ textAlign: "center", padding: "16px 0" }}>
          <div style={{ fontSize: 44, marginBottom: 16 }}>🏠</div>
          <h2 style={{ fontFamily: "'DM Serif Display', serif", fontWeight: 400, fontSize: 26, color: "#163C2F", margin: "0 0 10px" }}>
            Welcome home!
          </h2>
          <p style={{ fontSize: 14, color: "#9B9690", lineHeight: 1.6, margin: 0 }}>
            Your account is ready. Taking you to the tenant portal…
          </p>
        </div>
      ) : (
        <>
          <h1 style={{ fontFamily: "'DM Serif Display', serif", fontWeight: 400, fontSize: 28, color: "#2B2B2B", margin: "0 0 6px" }}>
            Create your account
          </h1>
          <p style={{ fontSize: 13, color: "#9B9690", margin: "0 0 24px", lineHeight: 1.6 }}>
            Set up your password to access your tenant portal — view payments, submit maintenance requests, and stay connected with your landlord.
          </p>

          <div style={{ background: "#F5F1EA", borderRadius: 8, padding: "10px 14px", marginBottom: 24, display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 13 }}>✉️</span>
            <span style={{ fontSize: 13, color: "#2B2B2B" }}>
              Creating account for <strong>{email}</strong>
            </span>
          </div>

          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            <div>
              <label style={lbl}>Full Name</label>
              <input
                type="text"
                value={fullName}
                onChange={e => setFullName(e.target.value)}
                style={field}
                placeholder="e.g. Marcus Johnson"
                autoFocus
                required
              />
            </div>

            <div>
              <label style={lbl}>Password</label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                style={field}
                placeholder="At least 8 characters"
                required
              />
            </div>

            <div>
              <label style={lbl}>Confirm Password</label>
              <input
                type="password"
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                style={field}
                placeholder="Re-enter your password"
                required
              />
            </div>

            {error && (
              <div style={{ background: "#FEF2F2", border: "1px solid #FCA5A5", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#dc2626" }}>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={saving}
              style={{
                background: saving ? "#9B9690" : "#163C2F",
                color: "#F5F1EA", border: "none", borderRadius: 999,
                padding: "14px 0", fontFamily: "'Manrope', sans-serif",
                fontSize: 13, fontWeight: 700,
                cursor: saving ? "not-allowed" : "pointer",
                textTransform: "uppercase", letterSpacing: "0.08em",
                transition: "background 0.15s", width: "100%",
              }}
            >
              {saving ? "Creating account…" : "Create Tenant Account"}
            </button>
          </form>
        </>
      )}
    </Shell>
  );
}

// ── Loading state while Supabase processes the invite token ───────────────────
function Splash() {
  return (
    <Shell>
      <div style={{ textAlign: "center", padding: "20px 0" }}>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <div style={{ width: 32, height: 32, border: "3px solid #E8E4DC", borderTop: "3px solid #163C2F", borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 20px" }} />
        <p style={{ fontSize: 14, color: "#9B9690", margin: 0 }}>Verifying your invitation…</p>
      </div>
    </Shell>
  );
}

// ── Invalid / expired link state ──────────────────────────────────────────────
function ExpiredLink() {
  return (
    <Shell>
      <div style={{ textAlign: "center", padding: "16px 0" }}>
        <div style={{ fontSize: 40, marginBottom: 16 }}>🔗</div>
        <h2 style={{ fontFamily: "'DM Serif Display', serif", fontWeight: 400, fontSize: 24, color: "#2B2B2B", margin: "0 0 10px" }}>
          Link expired or invalid
        </h2>
        <p style={{ fontSize: 14, color: "#9B9690", lineHeight: 1.65, margin: 0 }}>
          This setup link has already been used or has expired. Contact your landlord to request a new invitation.
        </p>
      </div>
    </Shell>
  );
}

// ── Coming Soon — registration not yet open ───────────────────────────────────
function ComingSoon() {
  return (
    <Shell>
      <div style={{ textAlign: "center", padding: "12px 0" }}>
        <div style={{ fontSize: 42, marginBottom: 18 }}>🏠</div>
        <h2 style={{ fontFamily: "'DM Serif Display', serif", fontWeight: 400, fontSize: 26, color: "#163C2F", margin: "0 0 10px" }}>
          Tenant Portal — Coming Soon
        </h2>
        <p style={{ fontSize: 14, color: "#9B9690", lineHeight: 1.65, margin: "0 0 22px" }}>
          The tenant portal isn't open for new registrations yet. If you already have an account, sign in using the button below.
        </p>
        <a
          href="/app?signin"
          style={{ display: "inline-block", background: "#163C2F", color: "#F5F1EA", borderRadius: 999, padding: "12px 28px", fontFamily: "'Manrope', sans-serif", fontSize: 13, fontWeight: 700, textDecoration: "none", letterSpacing: "0.07em", textTransform: "uppercase" }}
        >
          Sign In
        </a>
      </div>
    </Shell>
  );
}

// ── Shared page shell ─────────────────────────────────────────────────────────
function Shell({ children }) {
  return (
    <div style={{ minHeight: "100vh", background: "#F5F1EA", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "24px 16px", fontFamily: "'Manrope', sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;600;700&family=DM+Serif+Display:ital@0;1&family=Manrope:wght@400;500;600;700;800&display=swap" rel="stylesheet" />

      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 32 }}>
        <img src="/Leasly-logofinal.png" alt="Leasly" width="34" height="34" style={{ borderRadius: 7, objectFit: "contain" }} />
        <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 26, fontWeight: 700, color: "#163C2F", letterSpacing: "0.01em" }}>Leasly</span>
      </div>

      <div style={{ background: "#FFFFFF", border: "1px solid #E8E4DC", borderRadius: 16, padding: "36px 40px", width: "100%", maxWidth: 460, boxShadow: "0 2px 16px rgba(0,0,0,0.07)" }}>
        {children}
      </div>

      <p style={{ marginTop: 24, fontSize: 12, color: "#B0ACA6", textAlign: "center" }}>
        Need help? Contact your landlord directly.
      </p>
    </div>
  );
}
